<template>
  <LightBox dense class="mb-5">
    <FormHeading :h="5">
      <AppIcon icon="mdi:map-marker-outline" start />
      {{ label || $tt('home_address') }}
    </FormHeading>
    <AppSelect
      v-if="listVisible"
      v-model="selectedFromList"
      :placeholder="$tt('select_from_my_addresses')"
      :items="store.list"
      :item-title="stringifyAddress"
      prepend-icon="mdi:flash"
      class="list"
      return-object small-chips clearable
      @update:modelValue="autoset"
    />
    <v-expand-transition>
      <AppRow v-if="!selectedFromList">
        <TextField v-if="!hideLine" v-model="address.line" :label="$tt('street_number')" :rules="[required]" @change="addToList" />
        <TextField v-model="address.city" :label="$tt('city')" :rules="[required]" @change="addToList" />
        <CountrySelect v-model="address.country" :label="$tt('country')" @update:modelValue="addToList" />
      </AppRow>
    </v-expand-transition>
  </LightBox>
</template>

<script lang="ts">
import type { Address } from '~/types';

interface AddressStore {
  list: Address[];
  add: (address: Address) => void;
}

const MAX_ADDRESSES = 5;
const addressStore: AddressStore = {
  list: [],
  add (address: Address) {
    const included = this.list.some(a => a.line == address.line && a.city == address.city && a.country == address.country);
    if (included) return;
    this.list = this.list.filter(ad => ad.setBy !== address.setBy);
    this.list.push(address);
  }
};
</script>

<script setup lang="ts">
const address = defineModel<Address>({ required: true });
defineProps<{
  label?: string;
  hideLine?: boolean;
}>();

const { required } = useValidation();
const { tCountry } = useLocalization();

const uid = getCurrentInstance()?.uid;
const selectedFromList = ref(null);
const store = reactive(addressStore);

const listVisible = computed(() => {
  const { length } = store.list;
  if (!length) return false;
  if (length == 1 && store.list[0].setBy == uid) return false;
  return true;
});

onMounted(() => { 
  if (address.value) addToList();
});

function addToList() {
  const { line, city, country } = address.value;
  if (!line || !city || !country) return;
  if (store.list.length >= MAX_ADDRESSES) return;
  store.add({ line, city, country, setBy: uid });
}
function stringifyAddress({ line, city, country }: Address) {
  return `${line}, ${city}, ${tCountry(country)}`;
}
function autoset(newAddress?: Address) {
  if (!address.value) return;
  Object.assign(address.value, newAddress);
}
</script>

<style scoped>
.list {
  width: 50%;
  margin-top: var(--space-4);
}

@media (max-width: 850px) {
  .list {
    width: unset;
  }
}
</style>